import { forwardRef, type MouseEvent } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { useNavigateWithContentFlush } from '@/hooks/useNavigateWithContentFlush';

/**
 * Router Link that leaves a /content/ detail route without going stale.
 * `Link` navigates without flushSync and exposes no way to opt in, so plain
 * left-clicks are delegated to useNavigateWithContentFlush; modified clicks,
 * non-self targets, and reloadDocument keep native anchor behaviour.
 */
export const AppLink = forwardRef<HTMLAnchorElement, LinkProps>(function AppLink(props, ref) {
  const { to, replace, state, preventScrollReset, relative, reloadDocument, target, onClick } = props;
  const navigate = useNavigateWithContentFlush();

  if (reloadDocument) {
    return <Link ref={ref} {...props} />;
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (target !== undefined && target !== '_self')
    ) {
      return;
    }
    event.preventDefault();
    navigate(to, { replace, state, preventScrollReset, relative });
  };

  return <Link ref={ref} {...props} onClick={handleClick} />;
});
